<template>
    <div>
        <div class="p-4 bg-white shadow-md">
            <div class="flex justify-between items-center mb-4">
                <h2 class="text-xl font-semibold text-gray-800 flex items-center">
                    <span class="mr-2">🏫</span>
                    <span>High School Statistics</span>
                </h2>
                <div class="flex items-center gap-4">
                    <span class="text-sm text-gray-600">Other:</span>
                    <base-toggle :toggle="showOther" @set-checkbox-val="showOther = !showOther"></base-toggle>
                </div>
            </div>
            <div class="relative">
                <apexchart type="line" :options="chartOptions" :series="series" @dataPointSelection="handleBarClick">
                </apexchart>

                <!-- Loading overlay for chart -->
                <div v-if="isLoadingOtherDetails"
                    class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                    <div class="text-center">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        <p class="text-sm text-gray-600">Loading other school details...</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Other School Details Modal -->
        <base-modal v-if="showOtherDetailsModal" title="Other School Details" height="" @close="closeOtherDetailsModal">
            <div v-if="isLoadingOtherDetails" class="flex justify-center items-center py-8">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>

            <div v-else-if="otherSchoolDetails.length === 0" class="text-center py-8 text-gray-500">
                No other school details found.
            </div>

            <div v-else class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-100 sticky top-0 z-10">
                        <tr>
                            <th scope="col"
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                #
                            </th>
                            <th scope="col"
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                School Name
                            </th>
                            <th scope="col"
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Total Students
                            </th>
                            <th scope="col"
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Average Score
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-100">
                        <tr v-for="(school, index) in otherSchoolDetails" :key="index"
                            class="hover:bg-gray-50 transition-colors">
                            <td class="px-6 py-4 text-sm text-gray-900 font-medium">{{ index + 1 }}</td>
                            <td class="px-6 py-4 text-sm text-gray-900 font-medium">{{ school.school_name }}</td>
                            <td class="px-6 py-4 text-sm text-gray-700 font-semibold">{{ school.total_students }}</td>
                            <td class="px-6 py-4 text-sm text-indigo-600">{{ school.average_gpa }}%</td>
                        </tr>
                    </tbody>
                    <tfoot class="bg-gray-50">
                        <tr>
                            <td class="px-6 py-3 text-sm font-semibold text-gray-900 border-t-2 border-gray-200"></td>
                            <td class="px-6 py-3 text-sm font-semibold text-gray-900 border-t-2 border-gray-200">Total
                            </td>
                            <td class="px-6 py-3 text-sm font-semibold text-gray-700 border-t-2 border-gray-200">{{
                                totalStudents }}</td>
                            <td class="px-6 py-3 text-sm font-semibold text-indigo-600 border-t-2 border-gray-200">
                                {{
                                    averageGpa }}%</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </base-modal>
    </div>
</template>

<script>
import gql from 'graphql-tag';

const GET_OTHER_SCHOOL_DETAILS = gql`
  query GetOtherSchoolDetails($entity: String, $campus_code: [String], $local_or_non_local: String, $gender: String) {
    getOtherSchoolDetails(entity: $entity, campus_code: $campus_code, local_or_non_local: $local_or_non_local, gender: $gender) {
      error
      error_code
      message
      data {
        school_name
        total_students
        average_gpa
      }
    }
  }
`;

export default {
    name: "SchoolStatistics",
    props: ["data"],
    data() {
        return {
            showOther: true,
            series: [],
            showOtherDetailsModal: false,
            otherSchoolDetails: [],
            isLoadingOtherDetails: false,
            filteredData: [],
            chartOptions: {
                chart: {
                    height: 400,
                    type: "line",
                    stacked: false,
                    toolbar: {
                        show: false
                    }
                },
                stroke: {
                    width: [0, 4]
                },
                plotOptions: {
                    bar: {
                        columnWidth: "50%"
                    }
                },
                xaxis: {
                    categories: [],
                    labels: {
                        rotate: -45,
                        style: {
                            fontSize: "12px",
                            colors: "#4B5563" // Tailwind gray-600
                        }
                    }
                },
                yaxis: [
                    {
                        title: {
                            text: "Total Students",
                            style: { color: "#1F2937", fontWeight: 600 }
                        }
                    },
                    {
                        opposite: true,
                        title: {
                            text: "Average Score",
                            style: { color: "#1F2937", fontWeight: 600 }
                        }
                    }
                ],
                tooltip: {
                    shared: true,
                    intersect: false,
                    y: {
                        formatter: function (val) {
                            return val;
                        }
                    }
                },
                fill: {
                    opacity: [0.8, 1] // first series (bar) is semi-transparent, second series (line) is solid
                },
                colors: ["#4472C4", "#ED7D31"],
                legend: {
                    position: "top",
                    horizontalAlign: "center"
                }
            }
        };
    },
    computed: {
        totalStudents() {
            if (!this.otherSchoolDetails || this.otherSchoolDetails.length === 0) return 0;
            return this.otherSchoolDetails.reduce((sum, school) => sum + school.total_students, 0);
        },
        averageGpa() {
            if (!this.otherSchoolDetails || this.otherSchoolDetails.length === 0) return 0;
            const totalGpa = this.otherSchoolDetails.reduce((sum, school) => sum + (school.average_gpa || 0), 0);
            return (totalGpa / this.otherSchoolDetails.length).toFixed(2);
        }
    },
    watch: {
        data: {
            handler() {
                this.updateChart();
            },
            immediate: true,
            deep: true,
        },
        showOther() {
            this.updateChart();
        }
    },
    methods: {
        toggleOther() {
            this.showOther = !this.showOther;
        },
        updateChart() {
            if (!Array.isArray(this.data)) return;

            // filter based on toggle
            const filteredData = this.showOther
                ? this.data
                : this.data.filter(item => item.school_name !== "Other");

            const categories = filteredData.map(item => item.school_name);
            const totalStudents = filteredData.map(item => item.total_students);
            const averageScore = filteredData.map(item => item.average_gpa);

            this.chartOptions = {
                ...this.chartOptions,
                xaxis: { ...this.chartOptions.xaxis, categories },
            };

            this.series = [
                { name: "Total Students", type: "column", data: totalStudents },
                { name: "Average Score", type: "line", data: averageScore }
            ];

            // Store the filtered data for click handling
            this.filteredData = filteredData;
        },
        async handleBarClick(event, chartContext, config) {
            try {
                const dataPointIndex = config.dataPointIndex;

                // Get the label from the stored filtered data
                if (this.filteredData && this.filteredData.length > dataPointIndex) {
                    const clickedItem = this.filteredData[dataPointIndex];
                    const label = clickedItem.school_name;

                    if (label === 'Other') {
                        await this.fetchOtherSchoolDetails();
                    }
                } else {
                    console.warn('Filtered data not available or index out of bounds');
                    console.log('Available filtered data:', this.filteredData);
                }
            } catch (error) {
                console.error('Error in handleBarClick:', error);
            }
        },
        async fetchOtherSchoolDetails() {
            this.isLoadingOtherDetails = true;
            try {
                const response = await this.$apollo.query({
                    query: GET_OTHER_SCHOOL_DETAILS,
                    variables: {
                        entity: (this.$parent.filters && this.$parent.filters.entity) || 'FCHS',
                        campus_code: (this.$parent.filters && this.$parent.filters.campus_code) || [],
                        local_or_non_local: (this.$parent.filters && this.$parent.filters.local_or_non_local) || '',
                        gender: (this.$parent.filters && this.$parent.filters.gender) || '',
                    },
                    fetchPolicy: 'network-only'
                });

                if (!response.data.getOtherSchoolDetails.error) {
                    this.otherSchoolDetails = response.data.getOtherSchoolDetails.data;
                    this.showOtherDetailsModal = true;
                } else {
                    this.$swal({
                        icon: 'error',
                        title: 'Error',
                        text: response.data.getOtherSchoolDetails.message || 'Failed to fetch other school details'
                    });
                }
            } catch (error) {
                console.error('Failed to fetch other school details:', error);
                this.$swal({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to fetch other school details'
                });
            } finally {
                this.isLoadingOtherDetails = false;
            }
        },
        closeOtherDetailsModal() {
            this.showOtherDetailsModal = false;
            this.otherSchoolDetails = [];
        }
    },
};
</script>